/**
 * Bulk Import Page
 *
 * Batch document processing with:
 * - Drag-drop file upload (up to 200 documents)
 * - Real-time processing progress
 * - Review workflow for low-confidence items
 * - Cross-document correlation results
 *
 * @module pages/BulkImport
 */

import { useState, useCallback } from "react";
import { useCompany } from "@/contexts/CompanyContext";
import { trpc } from "@/lib/trpc";
import DashboardLayout from "@/components/DashboardLayout";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ScrollArea } from "@/components/ui/scroll-area";
import { FileDropZone } from "@/components/Discovery/FileDropZone";
import { ConfidenceBadge } from "@/components/Discovery/ConfidenceBadge";
import { PriorImportsPanel } from "@/components/CrossImportAwareness/PriorImportsPanel";
import {
  Upload,
  FolderOpen,
  Clock,
  CheckCircle,
  AlertCircle,
  XCircle,
  Loader2,
  Play,
  Pause,
  RefreshCw,
  FileText,
  Link2,
  AlertTriangle,
  ChevronRight,
  Eye,
  Check,
  X,
} from "lucide-react";
import { cn } from "@/lib/utils";

type JobStatus = "queued" | "processing" | "review" | "completed" | "failed";

interface BulkJob {
  id: number;
  name: string | null;
  status: JobStatus;
  totalDocuments: number;
  processedCount: number;
  successCount: number;
  errorCount: number;
  needsReviewCount: number;
  createdAt: number;
  completedAt?: number | null;
}

interface ReviewItem {
  documentId: number;
  documentType: string;
  confidence: number;
  entities: unknown;
  warnings: string[];
  errors: string[];
}

export default function BulkImport() {
  const { selectedCompanyId } = useCompany();
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<"upload" | "jobs" | "review">(
    "upload"
  );
  const [uploadedDocumentIds, setUploadedDocumentIds] = useState<number[]>([]);

  // Queries
  const jobsQuery = trpc.bulkImport.listJobs.useQuery(
    { companyId: selectedCompanyId || 0, limit: 20 },
    { enabled: !!selectedCompanyId, refetchInterval: 5000 }
  );

  const selectedJobQuery = trpc.bulkImport.getJob.useQuery(
    { jobId: selectedJobId || 0 },
    { enabled: !!selectedJobId, refetchInterval: selectedJobId ? 2000 : false }
  );

  const reviewItemsQuery = trpc.bulkImport.getReviewItems.useQuery(
    { jobId: selectedJobId || 0, threshold: 70 },
    {
      enabled:
        !!selectedJobId && selectedJobQuery.data?.job?.status === "review",
    }
  );

  const correlationsQuery = trpc.bulkImport.getCorrelations.useQuery(
    { jobId: selectedJobId || 0 },
    {
      enabled:
        !!selectedJobId &&
        (selectedJobQuery.data?.job?.status === "completed" ||
          selectedJobQuery.data?.job?.status === "review"),
    }
  );

  const estimateQuery = trpc.bulkImport.estimateTime.useQuery(
    {
      documentCount: uploadedDocumentIds.length,
      options: { autoMatchInvoices: true },
    },
    { enabled: uploadedDocumentIds.length > 0 }
  );

  // Cross-import context for intelligence sharing
  const {
    data: crossImportContext,
    refetch: refetchCrossImport,
    isLoading: isLoadingCrossImport,
  } = trpc.crossImport.getContext.useQuery(
    { companyId: selectedCompanyId || 0, currentImportPath: "bulk" },
    { enabled: !!selectedCompanyId }
  );

  // Mutations
  const uploadDocumentMutation = trpc.document.upload.useMutation();

  const createJobMutation = trpc.bulkImport.createJob.useMutation({
    onSuccess: data => {
      setSelectedJobId(data.jobId);
      setActiveTab("jobs");
      setUploadedDocumentIds([]); // Clear uploaded IDs after job creation
      jobsQuery.refetch();
    },
  });

  const startJobMutation = trpc.bulkImport.startJob.useMutation({
    onSuccess: () => {
      selectedJobQuery.refetch();
    },
  });

  const cancelJobMutation = trpc.bulkImport.cancelJob.useMutation({
    onSuccess: () => {
      jobsQuery.refetch();
      selectedJobQuery.refetch();
    },
  });

  const approveItemsMutation = trpc.bulkImport.approveReviewItems.useMutation({
    onSuccess: () => {
      reviewItemsQuery.refetch();
      selectedJobQuery.refetch();
    },
  });

  // State to track upload progress
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Handle files uploaded - convert to base64 and upload each file
  const handleFilesUploaded = useCallback(
    async (files: File[]) => {
      if (!selectedCompanyId || files.length === 0) return;

      setIsUploading(true);
      setUploadProgress(0);
      const documentIds: number[] = [];
      let processed = 0;

      for (const file of files) {
        try {
          // Convert file to base64
          const base64 = await new Promise<string>((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
              const result = reader.result as string;
              // Extract base64 data after the data URL prefix
              resolve(result);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });

          // Upload to server
          const result = await uploadDocumentMutation.mutateAsync({
            companyId: selectedCompanyId,
            fileName: file.name,
            fileData: base64,
            mimeType: file.type || "application/octet-stream",
            documentType: "bulk_import",
          });

          if (result.documentId) {
            documentIds.push(result.documentId);
          }
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
        }

        processed++;
        setUploadProgress(Math.round((processed / files.length) * 100));
      }

      setUploadedDocumentIds(prev => [...prev, ...documentIds]);
      setIsUploading(false);
    },
    [selectedCompanyId, uploadDocumentMutation]
  );

  // Create a new bulk import job
  const handleCreateJob = () => {
    if (!selectedCompanyId || uploadedDocumentIds.length === 0) return;

    createJobMutation.mutate({
      companyId: selectedCompanyId,
      documentIds: uploadedDocumentIds,
      name: `Bulk Import - ${new Date().toLocaleDateString("de-CH")}`,
      options: {
        reviewThreshold: 70,
        autoMatchInvoices: true,
        createVendorsAutomatically: false,
        skipDuplicates: true,
      },
    });
  };

  // Start processing a job
  const handleStartJob = (jobId: number) => {
    startJobMutation.mutate({ jobId });
  };

  // Cancel a job
  const handleCancelJob = (jobId: number) => {
    cancelJobMutation.mutate({ jobId });
  };

  // Approve review items
  const handleApproveItems = (
    documentIds: number[],
    action: "approve" | "reject" | "defer"
  ) => {
    if (!selectedJobId) return;
    approveItemsMutation.mutate({
      jobId: selectedJobId,
      documentIds,
      action,
    });
  };

  const getStatusBadge = (status: JobStatus) => {
    const variants: Record<
      JobStatus,
      { icon: React.ReactNode; className: string }
    > = {
      queued: {
        icon: <Clock className="h-3 w-3" />,
        className: "bg-muted text-muted-foreground",
      },
      processing: {
        icon: <Loader2 className="h-3 w-3 animate-spin" />,
        className: "bg-[var(--info)]/10 text-[var(--info)]",
      },
      review: {
        icon: <Eye className="h-3 w-3" />,
        className: "bg-[var(--warning)]/10 text-[var(--warning)]",
      },
      completed: {
        icon: <CheckCircle className="h-3 w-3" />,
        className: "bg-[var(--success)]/10 text-[var(--success)]",
      },
      failed: {
        icon: <XCircle className="h-3 w-3" />,
        className: "bg-destructive/10 text-destructive",
      },
    };
    const v = variants[status];
    return (
      <Badge variant="outline" className={cn("gap-1", v.className)}>
        {v.icon}
        <span className="capitalize">{status}</span>
      </Badge>
    );
  };

  if (!selectedCompanyId) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[50vh]">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>No company selected</AlertTitle>
            <AlertDescription>
              Please select a company to use bulk import.
            </AlertDescription>
          </Alert>
        </div>
      </DashboardLayout>
    );
  }

  const jobs = jobsQuery.data?.jobs || [];
  const selectedJob = selectedJobQuery.data?.job;
  const liveProgress = selectedJobQuery.data?.liveProgress;
  const reviewItems = reviewItemsQuery.data?.items || [];
  const correlations = correlationsQuery.data;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Bulk Document Import</h1>
            <p className="text-muted-foreground">
              Process up to 200 documents at once with intelligent extraction
              and correlation
            </p>
          </div>
          {jobs.length > 0 && (
            <Button variant="outline" onClick={() => jobsQuery.refetch()}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
          )}
        </div>

        {/* Cross-Import Intelligence Panel */}
        {selectedCompanyId && (
          <PriorImportsPanel
            currentImportPath="bulk"
            companyId={selectedCompanyId}
            priorDocuments={crossImportContext?.priorDocuments?.map(d => ({
              ...d,
              classification: d.classification ?? "unknown",
            }))}
            knownVendors={crossImportContext?.knownVendors}
            knownCustomers={crossImportContext?.knownCustomers}
            learnedPatterns={crossImportContext?.learnedPatterns}
            isLoading={isLoadingCrossImport}
            onRefresh={refetchCrossImport}
          />
        )}

        {/* Tabs */}
        <Tabs
          value={activeTab}
          onValueChange={v => setActiveTab(v as typeof activeTab)}
        >
          <TabsList className="grid w-full grid-cols-3 lg:w-[400px]">
            <TabsTrigger value="upload" className="gap-2">
              <Upload className="h-4 w-4" />
              Upload
            </TabsTrigger>
            <TabsTrigger value="jobs" className="gap-2">
              <FolderOpen className="h-4 w-4" />
              Jobs
              {jobs.filter(j => j.status === "processing").length > 0 && (
                <Badge
                  variant="secondary"
                  className="ml-1 h-5 w-5 p-0 justify-center"
                >
                  {jobs.filter(j => j.status === "processing").length}
                </Badge>
              )}
            </TabsTrigger>
            <TabsTrigger
              value="review"
              className="gap-2"
              disabled={!selectedJobId}
            >
              <Eye className="h-4 w-4" />
              Review
              {reviewItems.length > 0 && (
                <Badge
                  variant="destructive"
                  className="ml-1 h-5 w-5 p-0 justify-center"
                >
                  {reviewItems.length}
                </Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* Upload Tab */}
          <TabsContent value="upload" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Upload Documents</CardTitle>
                <CardDescription>
                  Drag and drop your documents for batch processing. Supports
                  bank statements, invoices, receipts, tax forms, and more.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <FileDropZone
                  onFilesSelected={handleFilesUploaded}
                  maxFiles={200}
                  isProcessing={isUploading}
                  acceptedFileTypes={[
                    "application/pdf",
                    "text/csv",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    "application/vnd.ms-excel",
                    "image/jpeg",
                    "image/png",
                  ]}
                />

                {/* Upload Progress */}
                {isUploading && (
                  <Alert>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <AlertTitle>Uploading Documents</AlertTitle>
                    <AlertDescription className="space-y-2">
                      <p>Uploading files... {uploadProgress}% complete</p>
                      <Progress value={uploadProgress} className="h-2" />
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
              {uploadedDocumentIds.length > 0 && !isUploading && (
                <CardFooter className="flex-col items-start gap-4 border-t pt-6">
                  <div className="flex items-center justify-between w-full">
                    <div>
                      <p className="font-medium">
                        {uploadedDocumentIds.length} documents ready
                      </p>
                      {estimateQuery.data && (
                        <p className="text-sm text-muted-foreground">
                          Estimated processing time: ~
                          {estimateQuery.data.estimatedMinutes} minutes
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleCreateJob}
                      disabled={createJobMutation.isPending}
                    >
                      {createJobMutation.isPending ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Play className="h-4 w-4 mr-2" />
                      )}
                      Start Processing
                    </Button>
                  </div>
                </CardFooter>
              )}
            </Card>
          </TabsContent>

          {/* Jobs Tab */}
          <TabsContent value="jobs" className="space-y-6">
            <div className="grid gap-6 lg:grid-cols-2">
              {/* Job List */}
              <Card>
                <CardHeader>
                  <CardTitle>Import Jobs</CardTitle>
                  <CardDescription>
                    Recent and active bulk import jobs
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ScrollArea className="h-[400px]">
                    {jobs.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-full text-center p-6">
                        <FolderOpen className="h-12 w-12 text-muted-foreground mb-4" />
                        <p className="text-muted-foreground">
                          No import jobs yet
                        </p>
                        <Button
                          variant="link"
                          onClick={() => setActiveTab("upload")}
                        >
                          Upload documents to start
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {jobs.map(job => (
                          <button
                            key={job.id}
                            onClick={() => setSelectedJobId(job.id)}
                            className={cn(
                              "w-full text-left p-4 rounded-lg border transition-colors",
                              selectedJobId === job.id
                                ? "border-primary bg-primary/5"
                                : "hover:bg-muted/50"
                            )}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium truncate">
                                {job.name || `Job #${job.id}`}
                              </span>
                              {getStatusBadge(job.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-muted-foreground">
                              <span>{job.totalDocuments} documents</span>
                              <span>•</span>
                              <span>
                                {new Date(job.createdAt).toLocaleDateString(
                                  "de-CH"
                                )}
                              </span>
                            </div>
                            {job.status === "processing" && (
                              <Progress
                                value={
                                  (job.processedCount / job.totalDocuments) *
                                  100
                                }
                                className="h-1 mt-2"
                              />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </CardContent>
              </Card>

              {/* Job Details */}
              <Card>
                <CardHeader>
                  <CardTitle>Job Details</CardTitle>
                  {selectedJob && (
                    <CardDescription>
                      {selectedJob.name || `Job #${selectedJob.id}`}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  {!selectedJob ? (
                    <div className="flex flex-col items-center justify-center h-[400px] text-center">
                      <ChevronRight className="h-12 w-12 text-muted-foreground mb-4" />
                      <p className="text-muted-foreground">
                        Select a job to view details
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Status & Progress */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium">Progress</span>
                          {getStatusBadge(selectedJob.status)}
                        </div>
                        <Progress
                          value={
                            (selectedJob.processedCount /
                              selectedJob.totalDocuments) *
                            100
                          }
                          className="h-2"
                        />
                        <p className="text-xs text-muted-foreground mt-1">
                          {selectedJob.processedCount} of{" "}
                          {selectedJob.totalDocuments} processed
                        </p>
                      </div>

                      {/* Live Progress Details */}
                      {liveProgress && (
                        <Alert>
                          <Loader2 className="h-4 w-4 animate-spin" />
                          <AlertTitle>Processing</AlertTitle>
                          <AlertDescription>
                            {liveProgress.currentDocument && (
                              <span>
                                Currently processing:{" "}
                                {liveProgress.currentDocument}
                              </span>
                            )}
                          </AlertDescription>
                        </Alert>
                      )}

                      {/* Stats Grid */}
                      <div className="grid grid-cols-2 gap-4">
                        <div className="p-3 rounded-lg bg-[var(--success)]/10">
                          <div className="flex items-center gap-2 text-[var(--success)]">
                            <CheckCircle className="h-4 w-4" />
                            <span className="font-medium">
                              {selectedJob.successCount}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Successful
                          </p>
                        </div>
                        <div className="p-3 rounded-lg bg-[var(--warning)]/10">
                          <div className="flex items-center gap-2 text-[var(--warning)]">
                            <AlertTriangle className="h-4 w-4" />
                            <span className="font-medium">
                              {selectedJob.needsReviewCount}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Needs Review
                          </p>
                        </div>
                        <div className="p-3 rounded-lg bg-destructive/10">
                          <div className="flex items-center gap-2 text-destructive">
                            <XCircle className="h-4 w-4" />
                            <span className="font-medium">
                              {selectedJob.errorCount}
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Errors
                          </p>
                        </div>
                        {correlations && (
                          <div className="p-3 rounded-lg bg-[var(--info)]/10">
                            <div className="flex items-center gap-2 text-[var(--info)]">
                              <Link2 className="h-4 w-4" />
                              <span className="font-medium">
                                {correlations.stats.total}
                              </span>
                            </div>
                            <p className="text-xs text-muted-foreground mt-1">
                              Correlations
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2">
                        {selectedJob.status === "queued" && (
                          <Button
                            onClick={() => handleStartJob(selectedJob.id)}
                            className="flex-1"
                          >
                            <Play className="h-4 w-4 mr-2" />
                            Start Processing
                          </Button>
                        )}
                        {selectedJob.status === "processing" && (
                          <Button
                            variant="destructive"
                            onClick={() => handleCancelJob(selectedJob.id)}
                            className="flex-1"
                          >
                            <Pause className="h-4 w-4 mr-2" />
                            Cancel
                          </Button>
                        )}
                        {selectedJob.status === "review" &&
                          selectedJob.needsReviewCount > 0 && (
                            <Button
                              onClick={() => setActiveTab("review")}
                              className="flex-1"
                            >
                              <Eye className="h-4 w-4 mr-2" />
                              Review Items ({selectedJob.needsReviewCount})
                            </Button>
                          )}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Correlations Summary */}
            {correlations && correlations.stats.total > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Link2 className="h-5 w-5" />
                    Document Correlations
                  </CardTitle>
                  <CardDescription>
                    Automatically detected relationships between documents
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                    <div className="p-4 rounded-lg border">
                      <p className="text-2xl font-bold">
                        {correlations.byType.invoice_payment.length}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Invoice → Payment Matches
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-2xl font-bold">
                        {correlations.byType.receipt_expense.length}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Receipt → Expense Links
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-2xl font-bold">
                        {correlations.byType.duplicate.length}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Potential Duplicates
                      </p>
                    </div>
                    <div className="p-4 rounded-lg border">
                      <p className="text-2xl font-bold text-[var(--warning)]">
                        {correlations.discrepancies.length}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Amount Discrepancies
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Review Tab */}
          <TabsContent value="review" className="space-y-6">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>Review Low-Confidence Items</CardTitle>
                    <CardDescription>
                      {reviewItems.length} items require manual review
                    </CardDescription>
                  </div>
                  {reviewItems.length > 0 && (
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleApproveItems(
                            reviewItems.map(i => i.documentId),
                            "approve"
                          )
                        }
                      >
                        <Check className="h-4 w-4 mr-1" />
                        Approve All
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          handleApproveItems(
                            reviewItems.map(i => i.documentId),
                            "defer"
                          )
                        }
                      >
                        <Clock className="h-4 w-4 mr-1" />
                        Defer All
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {reviewItems.length === 0 ? (
                  <div className="flex flex-col items-center justify-center h-[300px] text-center">
                    <CheckCircle className="h-12 w-12 text-[var(--success)] mb-4" />
                    <p className="text-lg font-medium">All items reviewed</p>
                    <p className="text-muted-foreground">
                      No more items need manual review
                    </p>
                  </div>
                ) : (
                  <ScrollArea className="h-[500px]">
                    <div className="space-y-4">
                      {reviewItems.map(item => (
                        <div
                          key={item.documentId}
                          className="p-4 rounded-lg border space-y-3"
                        >
                          <div className="flex items-start justify-between">
                            <div>
                              <div className="flex items-center gap-2">
                                <FileText className="h-4 w-4" />
                                <span className="font-medium">
                                  Document #{item.documentId}
                                </span>
                                <Badge variant="outline" className="capitalize">
                                  {item.documentType.replace(/_/g, " ")}
                                </Badge>
                              </div>
                              <div className="flex items-center gap-2 mt-1">
                                <ConfidenceBadge confidence={item.confidence} />
                                {item.warnings.length > 0 && (
                                  <span className="text-xs text-[var(--warning)]">
                                    {item.warnings.length} warning(s)
                                  </span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-[var(--success)]"
                                onClick={() =>
                                  handleApproveItems(
                                    [item.documentId],
                                    "approve"
                                  )
                                }
                              >
                                <Check className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-destructive"
                                onClick={() =>
                                  handleApproveItems(
                                    [item.documentId],
                                    "reject"
                                  )
                                }
                              >
                                <X className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>

                          {/* Warnings */}
                          {item.warnings.length > 0 && (
                            <div className="text-sm text-[var(--warning)] space-y-1">
                              {item.warnings.map((w, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <AlertTriangle className="h-3 w-3 mt-0.5" />
                                  <span>{w}</span>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Errors */}
                          {item.errors.length > 0 && (
                            <div className="text-sm text-destructive space-y-1">
                              {item.errors.map((e, i) => (
                                <div key={i} className="flex items-start gap-2">
                                  <XCircle className="h-3 w-3 mt-0.5" />
                                  <span>{e}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </DashboardLayout>
  );
}
